import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { useAuth } from '../../hooks/useAuth';
import { Loading } from '../../components/Loading';
import Header from '../../components/Header';
import HeaderUser from '../../components/HeaderUser';
import Container from '../../components/Container';
import PopUpIntervention from '../../components/PopUpIntervention';
import BoxEditUser from './Boxes/BoxEditUser';
import BoxDeleteAccount from './Boxes/BoxDeleteAccount';
import BoxSignatures from './Boxes/BoxSignatures';
import BoxAccountB2B from './Boxes/BoxAccountB2B';
import BoxDownloadData from './Boxes/BoxDownloadData';
import BoxFamily from './Boxes/BoxFamily';
import BoxAssociateAccounts from './Boxes/BoxAssociateAccounts';
import BoxProviders from './Boxes/BoxProviders';
import BoxParentConsent from './Boxes/BoxParentalConsent';
import BoxSession from './Boxes/BoxSession';
import BoxCommunicationPreferences from './Boxes/BoxCommunicationPreferences';
import { Actions, Categories, Labels } from '../../types/gtm';
import { familyApi } from '../../services/family-api';
import BoxOffers from './Boxes/BoxOffers';
import BoxLoyalty from './Boxes/BoxLoyalty';
import BoxUnderageInfo from './Boxes/BoxUnderageInfo';
import {
  INTERVENTION_MOBILE_PHONE,
  INTERVENTION_CONFIRM_CPF,
  INTERVENTION_CONFIRM_MOBILE_PHONE,
  INTERVENTION_CPF,
} from '../../util/interventionsMessage';
import { api } from '../../services/api';
import { getDevice, getUserLocale } from '../../util/userInfo';

const ofAge = 18;

type InterventionMessage = {
  type: string;
  title: string;
  description: string;
  redirectModal: string;
  dialogTitle: string;
  dialogDescription: string;
};

const interventionTemplate = {
  type: '',
  title: '',
  description: '',
  redirectModal: '',
  dialogTitle: '',
  dialogDescription: '',
};

interface InterventionObject {
  interventionID: string;
  interventionName: string;
  interventionURL: string;
  type: string;
  fields: [key: string];
  message: string;
  validateExternal: boolean;
  validateMobilePhone: boolean;
}

const HomePage = () => {
  const {
    loading,
    error,
    success,
    authenticated,
    user,
    logout,
    subscription,
    homeInfo,
  } = useAuth();

  window.dataLayer = window.dataLayer || [];

  const [userType, setUserType] = useState<string>('no-family');
  const [alreadyCalled, setCalled] = React.useState<boolean>(false);
  const [interventionSelect, setInterventionSelect] =
    React.useState<InterventionMessage>(interventionTemplate);
  const token = localStorage.getItem('token_jwt');

  const hasCellhone = () => {
    return (
      user.hideMobilePhoneIntervention ||
      (user.phones && user.phones.cellPhone && user.phones.dddCellPhone)
    );
  };

  const hasCellhoneConfirmed = () => {
    return (
      user.hideMobilePhoneIntervention ||
      (hasCellhone() && user.phones?.mobilePhoneConfirmed)
    );
  };

  const showDynamicIntervention = (interventions: any) => {
    const randomicIntervention = Math.floor(
      Math.random() * interventions.length,
    );
    setInterventionSelect(interventions[randomicIntervention]);
    return interventions[randomicIntervention];
  };

  const mountInterventionFields = (fieldsObject: InterventionObject): any => {
    const intervArray: any[] = [];
    const isMobileConfirmation = fieldsObject?.validateMobilePhone;
    const validateExternal = fieldsObject?.validateExternal;
    let showCPFIntervention = false;
    let showMobileIntervention = false;

    if (fieldsObject.fields) {
      for (let i = 0; i < fieldsObject.fields.length; i += 1) {
        if (fieldsObject.fields[i] === 'docs') {
          intervArray.push(INTERVENTION_CPF);
          showCPFIntervention = true;
        }
        if (fieldsObject.fields[i] === 'cellPhone') {
          intervArray.push(INTERVENTION_MOBILE_PHONE);
          showMobileIntervention = true;
        }
      }
    }

    if (validateExternal && !showCPFIntervention) {
      intervArray.push(INTERVENTION_CONFIRM_CPF);
    }

    if (isMobileConfirmation && !showMobileIntervention) {
      intervArray.push(INTERVENTION_CONFIRM_MOBILE_PHONE);
    }

    return showDynamicIntervention(intervArray);
  };

  const paramsGetIntervention = {
    device: getDevice(),
    country: getUserLocale(),
  };

  useEffect(() => {
    let isMounted = true;

    const getUserType = async () => {
      familyApi.get('/v2/fe/user/type').then((response: AxiosResponse) => {
        if (isMounted) {
          setUserType(response.data.type);
        }
      });
    };

    const getIntervention = async () => {
      api
        .get(`/user/intervention/${paramsGetIntervention.device}/BR`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        .then((response: any) => {
          if (
            response.status === 200 &&
            Object.keys(response.data).length !== 0
          ) {
            mountInterventionFields(response.data);
          }
        });
    };

    getUserType();
    getIntervention();

    if (isMounted) {
      if (user.globoId && !alreadyCalled) {
        setCalled(!alreadyCalled);
        window.dataLayer.push({
          event: 'customEventUserInteraction',
          event_category: Categories.Home,
          event_action: Actions.Pageview,
          event_label: Labels.Home,
          bd_suser_code: user?.globoId,
        });

        if (!hasCellhoneConfirmed()) {
          window.dataLayer.push({
            event: 'customEventUserInteraction',
            event_category: Categories.Home,
            event_action: Actions.InterventionAlert,
            event_label: 'Alerta Intervencao',
            bd_suser_code: user?.globoId,
          });
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, [user]);

  if (error && error !== '') {
    toast.error(`${error}`);
  }

  if (success && success !== '') {
    toast.success(`${success}`);
  }

  const getUserAge = (birthDate: string) => {
    return moment().diff(moment(birthDate, 'DD-MM-YYYY'), 'years');
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.innerHTML = `
    try {
      const cookieBanner = new LGPD.CookieBanner();
    } catch(err) {
      console.error(err);
    }
    `;
    setTimeout(() => {
      document.body.appendChild(script);
    }, 2500);
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
      />
      {loading && <Loading />}
      {authenticated && (
        <>
          <Header user={user} onLogout={logout} showReturn={false} />
          <HeaderUser user={user} onLogout={logout} />
          <Container>
            {interventionSelect.type === INTERVENTION_MOBILE_PHONE.type && (
              <PopUpIntervention
                messageIntervention={INTERVENTION_MOBILE_PHONE}
                user={user}
              />
            )}
            {interventionSelect.type === INTERVENTION_CPF.type && (
              <PopUpIntervention
                messageIntervention={INTERVENTION_CPF}
                user={user}
              />
            )}
            {interventionSelect.type ===
              INTERVENTION_CONFIRM_MOBILE_PHONE.type && (
              <PopUpIntervention
                messageIntervention={INTERVENTION_CONFIRM_MOBILE_PHONE}
                user={user}
              />
            )}
            {interventionSelect.type === INTERVENTION_CONFIRM_CPF.type && (
              <PopUpIntervention
                messageIntervention={INTERVENTION_CONFIRM_CPF}
                user={user}
              />
            )}
            {homeInfo?.componentLoyalty?.show && <BoxLoyalty />}
            {subscription?.subscriber && <BoxSignatures />}
            {subscription.status === 'FREE' &&
              getUserAge(user.birthDate) >= ofAge && <BoxOffers />}
            <BoxEditUser />
            {userType !== 'no-family' && <BoxFamily />}
            {homeInfo?.componentAccountInfoB2B?.show && <BoxAccountB2B />}
            {homeInfo?.componentUnderage?.show && (
              <BoxUnderageInfo info={homeInfo.componentUnderage.data} />
            )}
            {homeInfo?.componentConsent?.show && (
              <BoxParentConsent
                underageUsers={homeInfo.componentConsent.data}
              />
            )}
            <BoxAssociateAccounts />
            {homeInfo?.componentProviders?.show && (
              <BoxProviders providersInfo={homeInfo.componentProviders.data} />
            )}

            {homeInfo?.componentCommunicationPreference?.show && (
              <BoxCommunicationPreferences />
            )}

            {homeInfo?.componentSession?.show && (
              <BoxSession
                sessionInfo={homeInfo.componentSession.data}
                isBetaUser={homeInfo.componentSession.betaUser}
              />
            )}
            <BoxDownloadData />
            {homeInfo?.componentDelete?.show && <BoxDeleteAccount />}
          </Container>
        </>
      )}
    </>
  );
};

export default HomePage;
